import React from "react";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import useChartEventHandler from "@ui/Highcharts/hooks/useChartEventHandler";
import useSelectedAnalyses from "../../Analysis/useSelectedAnalyses";
import type { Analysis } from "../../../../api/Analysis";

function useAnalysisKeyboardSelect({
  projectId,
  chartRef,
  analyses,
}: {
  projectId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  analyses: Analysis[];
}) {
  const { boundary, setBoundary } = useSelectedAnalyses(projectId, analyses);

  const nextAnalysis = React.useCallback(
    (reverse: boolean): Analysis | null => {
      const { pivot } = boundary;
      return pivot === null
        ? null
        : reverse
          ? (analyses
              .slice()
              .reverse()
              .find(a => a.date < pivot) ?? null)
          : (analyses.find(a => a.date > pivot) ?? null);
    },
    [analyses, boundary],
  );

  const centerAnalysisIfNeeded = React.useCallback(
    (analysis: Analysis) => {
      const chart = chartRef.current;
      if (!chart) {
        throw new Error("chart not found");
      }
      const selectedDate = analysis.date;
      const xAxis = chart.xAxis[0];
      if (!xAxis) throw new Error("Missing xAxis");
      const { min, max, dataMin, dataMax } = xAxis.getExtremes();
      if (selectedDate >= min && selectedDate <= max) return; // date in viewport, no need to move
      const width = max - min;
      // center date in viewport, keeping existing viewport width
      let newMin = selectedDate - width / 2;
      let newMax = selectedDate + width / 2;
      if (newMin < dataMin) {
        newMin = dataMin;
        newMax = newMin + width; // restore full width
      }
      if (newMax > dataMax) {
        newMax = dataMax;
        newMin = newMax - width; // restore full width
      }
      xAxis.setExtremes(newMin, newMax, true, false, {
        trigger: "zoom",
      });
    },
    [chartRef],
  );

  const keyDownHandler = useDebouncedCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight": {
          const analysis = nextAnalysis(false);
          if (!analysis) return;
          centerAnalysisIfNeeded(analysis);
          setBoundary({ pivot: analysis.date, to: null });
          break;
        }
        case "ArrowLeft": {
          const analysis = nextAnalysis(true);
          if (!analysis) return;
          centerAnalysisIfNeeded(analysis);
          setBoundary({ pivot: analysis.date, to: null });
          break;
        }
        default:
          return;
      }
    },
    [nextAnalysis, setBoundary, centerAnalysisIfNeeded],
    100,
    { leading: true, maxWait: 100 },
  );

  useChartEventHandler(
    chartRef,
    "keydown",
    keyDownHandler as Highcharts.EventCallbackFunction<Highcharts.Chart>,
  );
}

export default useAnalysisKeyboardSelect;
